import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from '../lib/supabaseClient';
import { getSupabaseConfig } from '../config';
import {
  Announcement,
  AnnouncementKind,
  CreateAnnouncementResult,
  DuplicateAnnouncementError,
  OfflineOrNetworkError,
  Park,
  UUID,
} from './types';

// Public interface
export interface AnnouncementsData {
  listParks(): Promise<Park[]>;
  createAnnouncement(
    parkId: UUID,
    when: 'now' | Date
  ): Promise<CreateAnnouncementResult>;
  cancelAnnouncement(id: UUID): Promise<void | OfflineOrNetworkError>;
}

// Factory
let cached: AnnouncementsData | null = null;
export function getAnnouncementsData(): AnnouncementsData {
  if (cached) return cached;
  const { url, anonKey } = getSupabaseConfig();
  if (url && anonKey) {
    cached = new SupabaseAnnouncements();
  } else {
    cached = new MemoryAnnouncements();
  }
  return cached;
}

// Helpers
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function toISO(d: Date | null): string | null {
  return d ? d.toISOString() : null;
}

function fromISO(s: string | null | undefined): Date | null {
  return s ? new Date(s) : null;
}

// Supabase implementation
class SupabaseAnnouncements implements AnnouncementsData {
  private client: SupabaseClient;
  private familyId: string | null = null;

  constructor() {
    const sb = getSupabase();
    if (!sb) throw new Error('backend_unconfigured');
    this.client = sb as unknown as SupabaseClient;
  }

  private async ensureFamilyId(): Promise<string> {
    if (this.familyId) return this.familyId;
    const userRes = await this.client.auth.getUser();
    const user = userRes.data.user;
    if (!user) throw new Error('not signed in');
    const { data, error } = await this.client
      .from('profiles')
      .select('family_id')
      .eq('id', user.id)
      .single();
    if (error || !data) throw error ?? new Error('profile not found');
    const fid = (data as any).family_id as string | null;
    if (!fid) throw new Error('profile not found');
    this.familyId = fid;
    return fid;
  }

  async listParks(): Promise<Park[]> {
    const { data, error } = await this.client
      .from('parks')
      .select('id,name,sort_order')
      .order('sort_order', { ascending: true });
    if (error) throw error;
    return (data ?? []).map((p) => ({
      id: p.id as UUID,
      name: p.name as string,
      sortOrder: (p as any).sort_order as number,
    }));
  }

  async createAnnouncement(
    parkId: UUID,
    when: 'now' | Date
  ): Promise<CreateAnnouncementResult> {
    if (isOffline()) {
      return { type: 'offline', message: "You're offline" };
    }
    try {
      const familyId = await this.ensureFamilyId();
      const kind: AnnouncementKind = when === 'now' ? 'now' : 'later';
      const plannedAt = when === 'now' ? null : (when as Date);

      const insert = {
        family_id: familyId,
        park_id: parkId,
        kind,
        planned_at: toISO(plannedAt),
      };
      const { data, error } = await this.client
        .from('announcements')
        .insert(insert)
        .select('id, park_id, kind, planned_at, created_at, ended_at')
        .single();
      if (error) {
        // Map DB duplicate rate-limit error to a typed duplicate result
        if (
          typeof error.message === 'string' &&
          /too_many_announcements/i.test(error.message)
        ) {
          // Attempt to fetch the last post time for this park & family
          const { data: prev, error: prevErr } = await this.client
            .from('announcements')
            .select('created_at')
            .eq('family_id', familyId)
            .eq('park_id', parkId)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
          const duplicate: DuplicateAnnouncementError = {
            type: 'duplicate',
            previousCreatedAt: prevErr ? null : fromISO(prev?.created_at ?? null),
          };
          return duplicate;
        }
        return { type: 'network', message: 'Failed to post' };
      }
      const ann: Announcement = {
        id: data.id as UUID,
        parkId: data.park_id as UUID,
        kind: data.kind as AnnouncementKind,
        plannedAt: fromISO(data.planned_at),
        createdAt: new Date(data.created_at),
        endedAt: fromISO(data.ended_at),
      };
      return { type: 'success', announcement: ann };
    } catch {
      return { type: 'network', message: 'Failed to post' };
    }
  }

  async cancelAnnouncement(id: UUID): Promise<void | OfflineOrNetworkError> {
    if (isOffline()) return { type: 'offline', message: "You're offline" };
    try {
      const { error } = await this.client.rpc('end_announcement', {
        aid: id,
        reason: 'cancelled',
      } as any);
      if (error) return { type: 'network', message: 'Failed to cancel' };
    } catch {
      return { type: 'network', message: 'Failed to cancel' };
    }
  }
}

// In-memory fake (used when env is missing and in tests)
class MemoryAnnouncements implements AnnouncementsData {
  private parks: Park[] = [
    // Seeded order from schema; display name tweak for UX
    { id: 'park-flanders' as UUID, name: 'Flanders Park', sortOrder: 10 },
    { id: 'park-pocket' as UUID, name: 'The Pocket Park', sortOrder: 20 },
  ];
  private announcements: Announcement[] = [];

  async listParks(): Promise<Park[]> {
    return this.parks.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  }

  async createAnnouncement(
    parkId: UUID,
    when: 'now' | Date
  ): Promise<CreateAnnouncementResult> {
    if (isOffline()) {
      return { type: 'offline', message: "You're offline" };
    }
    const now = new Date();
    // 15-minute duplicate window
    const recent = this.announcements
      .filter((a) => a.parkId === parkId)
      .filter((a) => now.getTime() - a.createdAt.getTime() < 15 * 60 * 1000)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())[0];
    if (recent) {
      return { type: 'duplicate', previousCreatedAt: recent.createdAt };
    }
    const kind: AnnouncementKind = when === 'now' ? 'now' : 'later';
    const plannedAt = when === 'now' ? null : (when as Date);
    const createdAt = new Date();
    const ann: Announcement = {
      id: (`mem-${Math.random().toString(36).slice(2)}`) as UUID,
      parkId,
      kind,
      plannedAt,
      createdAt,
      endedAt: null,
    };
    this.announcements.push(ann);
    return { type: 'success', announcement: ann };
  }

  async cancelAnnouncement(id: UUID): Promise<void | OfflineOrNetworkError> {
    if (isOffline()) return { type: 'offline', message: "You're offline" };
    const found = this.announcements.find((a) => a.id === id);
    if (found) {
      found.endedAt = new Date();
    }
  }
}

